import { useState } from "react";
import { DialogTitle } from "../shared/modal";
import { ErrorContent } from "../shared/error-content";
import { LoadingContent } from "../shared/loading-content";
import { EditColorCollectionForm } from "../color-collections/edit-color-collection-form";
import { useSaveAsNewCollectionModalStore } from "../../../js/stores/save-as-new-collection-modal-store";
import {
  useCollectionSaveForm,
  useSaveCollection,
} from "../../../js/api/collection-save/collection-save";
import { useMainReact } from "../../main-react";
import { announce } from "../../../js/components/announce";
import { toast } from "../../../js/components/toast";

/**
 * "Save as new collection" — name and room-type a copy of somebody else's collection.
 *
 * The form itself is `EditColorCollectionForm`, unchanged. The server hands back the same
 * `CollectionFormResponse` descriptor the create/edit collection modal already renders — the same
 * fields, the same validation rules, the same server-error mapping — with the values prefilled from
 * the source collection and a different action URL. Building a second form for two fields we
 * already have a form for would be two sets of validation to keep in step.
 */
export function SaveAsNewCollectionContent({ closeClickHandler }) {
  const formUrl = useSaveAsNewCollectionModalStore((state) => state.formUrl);
  const collectionId = useSaveAsNewCollectionModalStore((state) => state.collectionId);

  const { generalServerErrorMessage, collectionSave } = useMainReact() ?? {};

  const { data, isLoading, isError } = useCollectionSaveForm(formUrl);
  const [serverErrors, setServerErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const saveMutation = useSaveCollection();

  const submitFormHandler = (body) => {
    if (!data?.mainAction?.linkUrl || !body) return;

    setIsSubmitting(true);
    setServerErrors({});

    saveMutation.mutate({
      url: data.mainAction.linkUrl,
      body,
      successHandler: (response) => {
        setIsSubmitting(false);

        if (!response?.isSuccess) {
          // Field-level rejections belong on the fields; anything else is a message.
          if (response?.errors) {
            setServerErrors(response.errors);
            return;
          }

          const message = response?.message ?? generalServerErrorMessage;
          toast.error(message);
          announce(message);
          return;
        }

        toast.success(response.message);
        announce(response.message);

        window.dispatchEvent(
          new CustomEvent("valspar:collection-saved", {
            detail: { collectionId, likesCount: response.likesCount },
          })
        );

        closeClickHandler();

        // Straight to the copy they just made. It is theirs now, it is private, and this is where
        // they would go next to publish or edit it.
        if (response.redirectUrl && window?.location?.href) {
          window.location.href = response.redirectUrl;
        }
      },
      errorHandler: (refusal) => {
        setIsSubmitting(false);

        if (refusal.errors) {
          setServerErrors({ ...refusal.errors });
          return;
        }

        const message = refusal.isRateLimited
          ? collectionSave?.rateLimitedMessage
          : refusal.message ?? generalServerErrorMessage;

        toast.error(message);
        announce(message);
      },
    });
  };

  if (isLoading || isSubmitting) {
    return <LoadingContent />;
  }

  if (isError) {
    return <ErrorContent />;
  }

  return (
    <div className="edit-color-collection-wrapper">
      <div className="edit-color-collection-content">
        <DialogTitle className="edit-color-collection-title">{data?.modalLabel}</DialogTitle>
        {data?.fields?.length > 0 && (
          <EditColorCollectionForm
            submitHandler={submitFormHandler}
            data={data}
            serverErrors={serverErrors}
            setServerErrors={setServerErrors}
            closeClickHandler={closeClickHandler}
          />
        )}
      </div>
    </div>
  );
}
